/**
 * Utility functions index - exports all utility functions
 * This is the main entry point for all utility functions in the application
 */

// Device utilities
export * from './device'

// Date utilities
export * from './date'

// Currency utilities
export * from './currency'

// Error utilities
export * from './error'

// Status utilities
export * from './status'

// Image utilities
export * from './image'

// Payment action utilities
export * from './paymentAction'

// DOM utilities
export * from './dom'

// Storage utilities
export * from './storage'

// Validation utilities
export * from './validation'

export * from './video'

// Class name utility (Tailwind-aware)
export * from './cn'

// App-specific utilities
export * from './cssVariables'
export { checkAndApplyFontSize, recalculateFontSizes, updateFontSizes, setupFontSizeListeners } from './fontSizing'
export {
  isEmbedded as isEmbeddedNew,
  setupModalDetection,
  setupIframeMessageListeners,
  addMobileDeviceDetection,
  postCustomerStatus,
} from './iframe'
export * from './translation'

// Session utilities
export * from './session'

// Re-export specific helpers to avoid conflicts
export { debounce, getTrimmedValue, stopMouseflowRecording, formatCurrency, prepareCartItems } from './helpers'

// Additional utility functions that didn't fit in other categories

/**
 * Get ReCaptcha headers for API requests
 */
export function getReCaptchaHeaders(params: Record<string, any>): Record<string, string> {
  const headers: Record<string, string> = {}

  if (params.recaptcha_token) {
    headers['X-Recaptcha-Token'] = params.recaptcha_token
  }

  return headers
}

/**
 * Get bundle quantity from item
 */
export function getBundleQuantity(item: any, useSubscription: boolean = false): number {
  if (!item) {
    return 1
  }

  // Logic for bundle quantity
  if (useSubscription && item.subscription_quantity) {
    return item.subscription_quantity
  }

  return item.quantity || 1
}

/**
 * Get bundle unit price from item
 */
export function getBundleUnitPrice(item: any, useSubscription: boolean = false): number {
  if (!item) {
    return 0
  }

  const quantity = getBundleQuantity(item, useSubscription)
  const totalPrice = useSubscription ? item.subscription_price || item.price : item.price

  return quantity > 0 ? totalPrice / quantity : totalPrice || 0
}

/**
 * Utility function to safely parse JSON
 */
export function safeJsonParse<T>(json: string, defaultValue: T): T {
  try {
    return JSON.parse(json)
  } catch {
    return defaultValue
  }
}

/**
 * Utility function to safely stringify JSON
 */
export function safeJsonStringify(obj: any, space?: number): string {
  try {
    return JSON.stringify(obj, null, space)
  } catch {
    return '{}'
  }
}

/**
 * Delay function for async operations
 */
export function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

/**
 * Retry function with exponential backoff
 */
export async function retry<T>(fn: () => Promise<T>, maxAttempts: number = 3, baseDelay: number = 1000): Promise<T> {
  let attempt = 1

  while (attempt <= maxAttempts) {
    try {
      return await fn()
    } catch (error) {
      if (attempt === maxAttempts) {
        throw error
      }

      const delayMs = baseDelay * Math.pow(2, attempt - 1)
      await delay(delayMs)
      attempt++
    }
  }

  throw new Error('Max retry attempts exceeded')
}

/**
 * Deep clone utility
 */
export function deepClone<T>(obj: T): T {
  if (obj === null || typeof obj !== 'object') {
    return obj
  }

  if (obj instanceof Date) {
    return new Date(obj.getTime()) as unknown as T
  }

  if (obj instanceof Array) {
    return obj.map(item => deepClone(item)) as unknown as T
  }

  if (typeof obj === 'object') {
    const cloned = {} as T
    Object.keys(obj).forEach(key => {
      ;(cloned as any)[key] = deepClone((obj as any)[key])
    })
    return cloned
  }

  return obj
}

/**
 * Generate random ID
 */
export function generateId(length: number = 8): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  let result = ''
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return result
}

/**
 * Check if value is empty (null, undefined, empty string, empty array, empty object)
 */
export function isEmpty(value: any): boolean {
  if (value == null) {
    return true
  }
  if (typeof value === 'string') {
    return value.trim().length === 0
  }
  if (Array.isArray(value)) {
    return value.length === 0
  }
  if (typeof value === 'object') {
    return Object.keys(value).length === 0
  }
  return false
}

/**
 * Omit properties from object
 */
export function omit<T, K extends keyof T>(obj: T, keys: K[]): Omit<T, K> {
  const result = { ...obj }
  keys.forEach(key => delete result[key])
  return result
}

/**
 * Pick properties from object
 */
export function pick<T extends object, K extends keyof T>(obj: T, keys: K[]): Pick<T, K> {
  const result = {} as Pick<T, K>
  keys.forEach(key => {
    if (key in obj) {
      result[key] = obj[key]
    }
  })
  return result
}

/**
 * Group array by key
 */
export function groupBy<T>(array: T[], keyFn: (item: T) => string | number): Record<string, T[]> {
  return array.reduce(
    (groups, item) => {
      const key = keyFn(item).toString()
      if (!groups[key]) {
        groups[key] = []
      }
      groups[key].push(item)
      return groups
    },
    {} as Record<string, T[]>
  )
}

/**
 * Flatten array of arrays
 */
export function flatten<T>(arrays: T[][]): T[] {
  return arrays.reduce((acc, val) => acc.concat(val), [])
}

/**
 * Remove duplicates from array
 */
export function unique<T>(array: T[], keyFn?: (item: T) => any): T[] {
  if (!keyFn) {
    return [...new Set(array)]
  }

  const seen = new Set()
  return array.filter(item => {
    const key = keyFn(item)
    if (seen.has(key)) {
      return false
    }
    seen.add(key)
    return true
  })
}
